/**
 * <ApprovalFlowDiagram /> — vertical step-card flow with connecting
 * arrows (M21.13, criterion 21).
 *
 * Two modes:
 *   - config  → pass `steps`: renders the configured chain shape
 *     (kind + selector + guard + SLA). Used on the configurator.
 *   - resolved → pass `resolvedSteps`: renders the chain resolved
 *     against a specific request, with the active step highlighted
 *     in emerald and unresolved steps flagged. Used on workflow
 *     detail pages.
 */

import { ArrowDown, CheckCircle2, Circle, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ApprovalChainStep, ResolvedApprovalStep } from "../../lib/api";

const _KIND_KEY: Record<ApprovalChainStep["kind"], string> = {
  immediate_manager: "app.afd_kind_immediate",
  position: "app.afd_kind_position",
  employee: "app.afd_kind_employee",
};

function StepCard({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "active" | "failed";
}) {
  const toneClass =
    tone === "active"
      ? "border-emerald-300 bg-emerald-50"
      : tone === "failed"
        ? "border-rose-300 bg-rose-50"
        : "border-border-subtle bg-surface-default";
  return (
    <div className={`rounded-xl border p-3 text-sm ${toneClass}`}>
      {children}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex justify-center py-1" aria-hidden>
      <ArrowDown size={16} className="text-text-tertiary" />
    </div>
  );
}

export interface ApprovalFlowDiagramProps {
  /** Config mode — the chain shape as configured. */
  steps?: ApprovalChainStep[];
  /** Resolved mode — the chain resolved against a request. */
  resolvedSteps?: ResolvedApprovalStep[];
  /** In resolved mode, the 0-based index of the in-flight step. */
  activeStepIndex?: number;
}

export function ApprovalFlowDiagram({
  steps,
  resolvedSteps,
  activeStepIndex,
}: ApprovalFlowDiagramProps) {
  const { t } = useTranslation();
  if (resolvedSteps && resolvedSteps.length > 0) {
    return (
      <div className="flex flex-col">
        {resolvedSteps.map((s, i) => {
          const isActive = i === activeStepIndex;
          const tone = !s.resolved
            ? "failed"
            : isActive
              ? "active"
              : "neutral";
          return (
            <div key={s.step_index}>
              {i > 0 && <Connector />}
              <StepCard tone={tone}>
                <div className="flex items-center gap-2">
                  {!s.resolved ? (
                    <UserX size={14} className="text-rose-600" />
                  ) : isActive ? (
                    <Circle
                      size={14}
                      className="fill-emerald-500 text-emerald-600"
                    />
                  ) : (
                    <CheckCircle2
                      size={14}
                      className="text-text-tertiary"
                    />
                  )}
                  <span className="font-medium text-text-primary">
                    {t("app.afd_step_n", { n: i + 1 })}
                  </span>
                  <span className="text-text-secondary">
                    {s.step_kind.replace("_", " ")}
                  </span>
                </div>
                <p className="mt-1 ps-6 text-text-secondary">
                  {s.resolved
                    ? s.employee_name_en
                    : t("app.afd_unresolved", {
                        reason: s.failure_reason ?? t("app.afd_no_approver"),
                      })}
                </p>
              </StepCard>
            </div>
          );
        })}
      </div>
    );
  }

  const cfg = steps ?? [];
  if (cfg.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border-subtle p-4 text-center text-sm text-text-tertiary">
        {t("app.afd_no_steps")}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {[...cfg]
        .sort((a, b) => a.step_number - b.step_number)
        .map((s, i) => (
          <div key={s.step_number}>
            {i > 0 && <Connector />}
            <StepCard>
              <div className="flex items-center gap-2">
                <Circle size={14} className="text-text-tertiary" />
                <span className="font-medium text-text-primary">
                  {t("app.afd_step_n", { n: s.step_number })}
                </span>
                <span className="text-text-secondary">
                  {t(_KIND_KEY[s.kind])}
                </span>
                {s.parallel && (
                  <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] uppercase text-text-secondary">
                    {t("app.afd_parallel")}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 ps-6 text-xs text-text-secondary">
                {s.kind === "position" && s.position_id && (
                  <span>{t("app.afd_position_short", { id: s.position_id.slice(0, 8) })}</span>
                )}
                {s.kind === "employee" && s.employee_id && (
                  <span>{t("app.afd_employee_short", { id: s.employee_id.slice(0, 8) })}</span>
                )}
                {s.guard_expression && (
                  <span>{t("app.afd_guard_short", { expr: s.guard_expression })}</span>
                )}
                {s.sla_hours != null && <span>{t("app.afd_sla", { h: s.sla_hours })}</span>}
              </div>
            </StepCard>
          </div>
        ))}
    </div>
  );
}
